// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import ResponsiveButton, {
    ResponsiveButtonPresentationProps,
} from "@store/Library/Components/Toolkit/ResponsiveButton";
import getButtonVariantField from "@store/Framework/Common/Fields/Toolkit/buttonVariant";
import getButtonSizeVariantField from "@store/Framework/Common/Fields/Toolkit/buttonSizeVariant";
import { ButtonSizeVariants } from "@store/MobiusOverrides/Button/Button";
import QuoteMark from "@store/Library/Components/Icons/QuoteMark";

const enum fields {
    quoteText = "quoteText",
    author = "author",
    showCta = "showCta",
    ctaText = "ctaText",
    ctaLink = "ctaLink",
    ctaVariant = "ctaVariant",
    ctaSizeVariant = "ctaSizeVariant",
    textColor = "textColor",
    quoteIconColor = "quoteIconColor",
    customCSS = "customCSS",
}

const ctaVariantField = getButtonVariantField(fields.ctaVariant);
const ctaSizeVariantField = getButtonSizeVariantField(fields.ctaSizeVariant);

interface OwnProps extends WidgetProps {
    fields: {
        [fields.quoteText]: string;
        [fields.author]: string;
        [fields.showCta]: boolean;
        [fields.ctaText]: string;
        [fields.ctaLink]: LinkFieldValue;
        [fields.ctaVariant]: "primary" | "secondary" | "tertiary" | "inverted" | "invertedOutline";
        [fields.ctaSizeVariant]: ButtonSizeVariants;
        [fields.textColor]: string;
        [fields.quoteIconColor]: string;
        [fields.customCSS]: string;
    };
}

export interface TestimonialStyles {
    wrapper?: InjectableCss;
    layout?: InjectableCss;
    quoteMark?: InjectableCss;
    quoteText?: TypographyPresentationProps;
    author?: TypographyPresentationProps;
    ctaButton?: ResponsiveButtonPresentationProps;
}

export const testimonialStyles: TestimonialStyles = {
    wrapper: {
        css: spacingStyles(),
    },
    layout: {
        css: css`
            --testimonial-text: ${getColor("text")};
            --testimonial-quote: ${getColor("primary")};

            color: var(--testimonial-text);

            display: grid;
            justify-items: start;
            row-gap: ${pxToRem(16)};

            ${breakpoint("md")} {
                row-gap: ${pxToRem(16)};
            }
        `,
    },
    quoteMark: {
        css: css`
            width: ${pxToRem(48)};
            height: ${pxToRem(48)};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: var(--testimonial-quote);

            svg {
                width: 100%;
                height: 100%;
                display: block;
            }
        `,
    },
    quoteText: {
        variant: "h3",
        css: css`
            margin: 0;
            line-height: ${pxToRem(34)};
        `,
    },
    author: {
        variant: "p",
        css: css`
            margin: 0;
            opacity: 0.9;
            font-family: "Noto Sans", sans-serif;
        `,
    },
    ctaButton: {
        css: css`
            margin-top: ${pxToRem(16)};
            width: auto;
            align-self: flex-start;
            display: inline-flex;
        `,
        mergeCss: true,
    },
};

export const Testimonial: FC<OwnProps> = ({ fields }) => {
    const ctaLink = useGetLink(fields.ctaLink);
    const { isInShell } = useShellContext();

    const baseStyles = useMergeStyles("testimonial" as any, testimonialStyles);

    const styles = React.useMemo(() => {
        const fieldDrivenLayoutCss = css`
            ${fields.textColor
                ? css`
                      --testimonial-text: ${({ theme }) => resolveColor(fields.textColor, theme)};
                  `
                : ""}

            ${fields.quoteIconColor
                ? css`
                      --testimonial-quote: ${({ theme }) => resolveColor(fields.quoteIconColor, theme)};
                  `
                : ""}
        `;

        return mergeProps<TestimonialStyles>(baseStyles, {
            layout: {
                css: css`
                    ${fieldDrivenLayoutCss}
                `,
            },
        });
    }, [baseStyles, fields.textColor, fields.quoteIconColor]);

    const ctaButtonStyles: ResponsiveButtonPresentationProps = React.useMemo(() => {
        return {
            ...styles.ctaButton,
            variant: ctaVariantField.parse(fields.ctaVariant),
            sizeVariant: ctaSizeVariantField.parse(fields.ctaSizeVariant),
            mergeCss: true,
        };
    }, [fields.ctaVariant, fields.ctaSizeVariant, styles.ctaButton]);

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css}
            ${fields.customCSS}
        `,
    };

    const resolvedCtaUrl = ctaLink?.url || "";
    const shouldRenderCta = fields.showCta && !!fields.ctaText && (!!resolvedCtaUrl || isInShell);

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="layout" css={styles.layout?.css}>
                <StyledWrapper className="quoteMark" css={styles.quoteMark?.css}>
                    <QuoteMark />
                </StyledWrapper>
                {!!fields.quoteText && (
                    <Typography className="quoteText" {...styles.quoteText}>
                        {fields.quoteText}
                    </Typography>
                )}
                {!!fields.author && (
                    <Typography className="author" {...styles.author}>
                        {fields.author}
                    </Typography>
                )}
                {shouldRenderCta && (
                    <ResponsiveButton
                        className="ctaButton"
                        href={resolvedCtaUrl || "#"}
                        target="_self"
                        disabled={!resolvedCtaUrl && isInShell}
                        aria-disabled={!resolvedCtaUrl && isInShell}
                        {...ctaButtonStyles}
                    >
                        {fields.ctaText}
                    </ResponsiveButton>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.layout {
}

.quoteMark {
}

.quoteText {
}

.author {
}

.ctaButton {
}
`;

const basicTab = { displayName: "Basic", sortOrder: 0 };
const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Testimonial,
    definition: {
        displayName: "Testimonial",
        group: "Basic",
        icon: "quote-left",
        fieldDefinitions: [
            {
                name: fields.quoteText,
                displayName: "Testimonial Text",
                editorTemplate: "MultilineTextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.quoteIconColor,
                displayName: "Quote Mark Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.author,
                displayName: "Author",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.showCta,
                displayName: "Show CTA",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.ctaText,
                displayName: "CTA Text",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
                isVisible: x => !!x.fields.showCta,
                validate: (value, item: any) => {
                    if (!item?.fields?.showCta) return "";
                    return value ? "" : "CTA Text is required";
                },
            },
            {
                name: fields.ctaLink,
                displayName: "CTA Link",
                editorTemplate: "LinkField",
                fieldType: "Contextual",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
                isVisible: x => !!x.fields.showCta,
                validate: (value, item: any) => {
                    if (!item?.fields?.showCta) return "";
                    return value?.value ? "" : "CTA Link is required";
                },
            },
            {
                ...ctaVariantField.definition,
                fieldType: "General",
                tab: basicTab,
                isVisible: x => !!x.fields.showCta,
            },
            {
                ...ctaSizeVariantField.definition,
                tab: basicTab,
                isVisible: x => !!x.fields.showCta,
            },

            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors?.length) {
                        return result.stylesheet.parsingErrors.map(e => `${e.reason} on line ${e.line}`)[0];
                    }
                    return "";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
